import { useCallback, useState } from 'react';

/**
 * Opens a file the API prints, an incident record or a statement of account, in a new tab (R).
 *
 * Staff only (Q41): the API refuses a guardian, and no guardian screen offers one. The file is the nursery's own
 * copy, so it opens in the browser's viewer, where it can be printed, rather than through `noSave`.
 *
 * The bearer token cannot go on a link, so the file is fetched and shown from memory. The tab is opened before
 * the request, while the click still counts as the user's: a tab opened only once the file has arrived is a
 * popup the browser may block. The object URL is not revoked, because the tab may still be reading it; it goes
 * when this page does.
 */
export function usePrintedFile() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const open = useCallback(async (key: string, fetchFile: () => Promise<Blob>) => {
    setError(null);
    const view = window.open('', '_blank');
    setBusy(key);
    try {
      const url = URL.createObjectURL(await fetchFile());
      if (view) view.location.href = url;
      else window.open(url, '_blank');
    } catch (cause) {
      view?.close();
      setError(cause);
    } finally {
      // Only this file's own key: a second file asked for meanwhile is still being fetched.
      setBusy((current) => (current === key ? null : current));
    }
  }, []);

  return {
    /** Fetches the file and shows it; `key` names the row it was asked from, for `busy`. */
    open,
    /** The key of the file being fetched, or null. */
    busy,
    /** Why the last file could not be shown: the API's own problem, rendered with `ErrorState`. */
    error,
  };
}
